import { SuggestionsStrip } from "@/components/suggestions/SuggestionsStrip";
import { getSuggestionEventsSink, type RenderableSuggestion } from "@/domain/suggestions";
import { useOptimisticCart } from "@/hooks/use-optimistic-cart";
import { useUpsellSuggestions } from "@/hooks/use-suggestions";
import { buildSuggestionCartItem } from "@/lib/add-suggestion-to-cart";
import { getMerchantId } from "@/lib/company-storage";
import { suggestionMessage } from "@/lib/i18n/suggestions-messages";
import { markSuggestionNavigation } from "@/lib/suggestion-navigation";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * "Quem pediu isso também levou" below an item's detail. Anchor is the item
 * being viewed; excludes are the items already in the bag (they would be
 * dropped by the join anyway, but sending them keeps the ranking itself from
 * spending its 8 slots on something the customer cannot add again).
 */
export function ItemSuggestionsStrip({ viewedItemId, cartItemIds }: { viewedItemId: string; cartItemIds: string[] }) {
  const merchantId = getMerchantId();
  const { addItem } = useOptimisticCart();
  const [, setSearchParams] = useSearchParams();

  const anchorItemIds = useMemo(() => [viewedItemId], [viewedItemId]);
  const excludeItemIds = useMemo(() => cartItemIds, [cartItemIds]);
  const cartItemIdSet = useMemo(() => new Set(cartItemIds), [cartItemIds]);

  const { suggestions, combo, enabled } = useUpsellSuggestions(
    "ITEM",
    anchorItemIds,
    excludeItemIds,
    cartItemIdSet,
  );

  if (!enabled) return null;

  function handleAdd(suggestion: RenderableSuggestion) {
    addItem(buildSuggestionCartItem(suggestion, "ITEM"));
    if (merchantId) {
      getSuggestionEventsSink(merchantId).enqueue({
        type: "ADD",
        surface: "ITEM",
        itemIds: [suggestion.merchantItemId],
        arm: suggestion.arm,
        anchorItemId: suggestion.anchorItemId,
        items: [{ itemId: suggestion.merchantItemId, arm: suggestion.arm, anchorItemId: suggestion.anchorItemId }],
      });
    }
  }

  function openItem(itemOfferId: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("item", itemOfferId);
      return next;
    });
  }

  return (
    <SuggestionsStrip
      merchantId={merchantId}
      surface="ITEM"
      title={suggestionMessage("itemStripTitle")}
      suggestions={suggestions}
      combo={combo}
      onAdd={handleAdd}
      onOpenDetail={(suggestion) => {
        markSuggestionNavigation(suggestion.itemOfferId, "ITEM", suggestion.arm, suggestion.anchorItemId);
        openItem(suggestion.itemOfferId);
      }}
      onOpenCombo={openItem}
      anchorItemId={viewedItemId}
    />
  );
}
